/**
 * space_xr_flight.ts — the HEADSET flight map for the sector (space campaign L, 2026-10-01; gap 27). Pure.
 *
 * THE HEADSET RIDE's precedent (`gamepad_ride.padRideFromSnapshot`): while a headset presents, the XR module feeds the
 * controllers into the shared pad snapshot (`gamepad.padFeedXr`) and this reads it DIRECTLY — no new pad action, so
 * the binding table, B's per-frame validator and I's / G's action pins are untouched. Every press that is an ACTION
 * comes back as a `KeyActionName` the sector dispatches through its ONE dispatch (`SpaceModule.sectorAction`), so the
 * handler I or G owns runs — never a second copy.
 *
 * The layout (a space sim's conventions, as the desktop pad's: the throttle on one stick, the nose on the other):
 *   MOVE-hand stick   Y throttle (held, JTL) · X roll        (the comfort page's MOVE HAND swaps the sticks)
 *   TURN-hand stick   the nose (the pad's own look curve and rate)
 *   right trigger     fire (the active fire group's PRIMARY)   left trigger  boost
 *   grips (squeeze)   roll toward that hand
 *   A                 level the wings (held)                  B             the SECONDARY trigger (missile / jammer)
 *   Y                 cycle target                            X             the VR panel (the menu module's own)
 *   right-stick press countermeasures                         left-stick press  swap fire group
 * Triggers and face buttons stay with their physical hands (the ride's precedent: its throttle is rt − lt whichever
 * hand steers).
 */
import { PAD_BUTTON, PAD_LOOK_LINEARITY, PAD_LOOK_RATE } from './gamepad.js';
import { padSteerCurve } from './gamepad_ride.js';
import type { Hand } from './xr_comfort.js';
import type { KeyActionName } from './keybinds.js';

/** What we read off the shared snapshot (a plain object in the harness). */
export interface XrSnapLike {
  present: boolean;
  lx: number; ly: number; rx: number; ry: number; lt: number; rt: number;
  down: readonly boolean[]; edge: readonly boolean[];
}

export interface SpaceXrFlight {
  active: boolean;
  /** −1..1: the held-throttle ramp (+ = up). */
  throttle: number;
  /** −1..1: + rolls left (the sector's `rollIn` sign). */
  roll: number;
  /** −1..1 each: the nose stick through the look curve (× PAD_LOOK_RATE × dt = rad this tick). */
  lookX: number;
  lookY: number;
  fire: boolean;
  boost: boolean;
  level: boolean;
  /** The ACTIONS pressed this frame (rising edges), in dispatch order. */
  actions: KeyActionName[];
}

/** A stick past this is an input (the snapshot already applied the pad's own 0.12 deadzone). */
export const SPACE_XR_DEADZONE = 0.15;

/** The press-edge buttons and the sector action each dispatches. */
export const SPACE_XR_EDGE_ACTIONS: ReadonlyArray<readonly [number, KeyActionName]> = [
  [PAD_BUTTON.faceRight, 'spaceMissile'],          // B — the secondary trigger (space campaign I's fire group)
  [PAD_BUTTON.faceUp, 'spaceTarget'],              // Y — cycle target
  [PAD_BUTTON.stickR, 'spaceCountermeasures'],     // right-stick press — flares / the sensor burst
  [PAD_BUTTON.stickL, 'spaceFireGroup'],           // left-stick press — fire group 1 ↔ 2
];

const IDLE: SpaceXrFlight = Object.freeze({ active: false, throttle: 0, roll: 0, lookX: 0, lookY: 0, fire: false, boost: false, level: false, actions: [] }) as SpaceXrFlight;

const num = (v: number): number => (Number.isFinite(v) ? Math.max(-1, Math.min(1, v)) : 0);

export function spaceXrFlight(s: XrSnapLike | null | undefined, moveHand: Hand = 'left'): SpaceXrFlight {
  if (!s || !s.present) return IDLE;
  const mx = num(moveHand === 'right' ? s.rx : s.lx), my = num(moveHand === 'right' ? s.ry : s.ly);
  const tx = num(moveHand === 'right' ? s.lx : s.rx), ty = num(moveHand === 'right' ? s.ly : s.ry);
  const dz = (v: number): number => (Math.abs(v) > SPACE_XR_DEADZONE ? v : 0);
  const gripL = s.down[PAD_BUTTON.bumperL] === true, gripR = s.down[PAD_BUTTON.bumperR] === true;
  // A squeeze rolls toward that hand and wins over the stick (a deliberate grip).
  const roll = gripL !== gripR ? (gripL ? 1 : -1) : -dz(mx);
  const actions: KeyActionName[] = [];
  for (const [b, a] of SPACE_XR_EDGE_ACTIONS) if (s.edge[b] === true) actions.push(a);
  const out: SpaceXrFlight = {
    active: true,
    throttle: -dz(my),                                         // stick Y is DOWN-positive
    roll,
    lookX: tx === 0 ? 0 : padSteerCurve(tx, PAD_LOOK_LINEARITY),
    lookY: ty === 0 ? 0 : padSteerCurve(ty, PAD_LOOK_LINEARITY),
    fire: s.down[PAD_BUTTON.triggerR] === true,
    boost: s.down[PAD_BUTTON.triggerL] === true,
    level: s.down[PAD_BUTTON.faceDown] === true,
    actions,
  };
  return out;
}

/** The nose this tick (rad), the pad's own rate. */
export function spaceXrLook(f: SpaceXrFlight, dt: number): { dx: number; dy: number } {
  return { dx: f.lookX * PAD_LOOK_RATE * dt, dy: f.lookY * PAD_LOOK_RATE * dt };
}

/** A headset button's printed name (the Quest's own labels; the map above is the headset's, like the HEADSET RIDE's). */
const XR_BUTTON_NAME: Readonly<Record<number, string>> = {
  [PAD_BUTTON.faceRight]: 'B', [PAD_BUTTON.faceUp]: 'Y', [PAD_BUTTON.faceDown]: 'A',
  [PAD_BUTTON.stickR]: 'R-click', [PAD_BUTTON.stickL]: 'L-click',
};
const XR_ACTION_WORD: Readonly<Partial<Record<KeyActionName, string>>> = {
  spaceMissile: 'missile', spaceTarget: 'target', spaceCountermeasures: 'flares', spaceFireGroup: 'group',
};
/** The headset button that dispatches `action` (read off the edge table — the legend can never drift from the map). */
export function xrButtonFor(action: KeyActionName): string {
  for (const [b, a] of SPACE_XR_EDGE_ACTIONS) if (a === action) return XR_BUTTON_NAME[b] ?? '?';
  return '';
}

/** The legend the cockpit HUD prints (the words follow the move hand; the buttons come from the edge table). */
export function spaceXrLegend(moveHand: Hand = 'left'): string[] {
  const mv = moveHand === 'right' ? 'Right' : 'Left', tn = moveHand === 'right' ? 'Left' : 'Right';
  const edges = SPACE_XR_EDGE_ACTIONS.map(([b, a]) => `${XR_BUTTON_NAME[b] ?? '?'} ${XR_ACTION_WORD[a] ?? a}`).join(' · ');
  return [
    `${mv} stick: throttle · roll   ${tn} stick: nose`,
    'R trigger fire · L trigger boost · grips roll',
    `${XR_BUTTON_NAME[PAD_BUTTON.faceDown]} level · ${edges} · X menu`,
  ];
}
